import {
    Action,
    setOperationErrors,
    BatchPageState,
    Fields,
    getInitialState,
    reducer,
    reset,
    updateAvailableAudioFormats,
    updateAvailableLanguages,
    updateItem,
    updateSelect
} from "./reducer"
import React, {
    createContext,
    FunctionComponent,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer
} from "react"
import moment from "moment"
import { GET_INIT_BATCH_INFO } from "../../queries/GET_INIT_BATCH_INFO"
import { ApolloError, FetchResult, useApolloClient, useQuery } from "@apollo/client"
import { AudioFile, AudioFormat, Language } from "../../../typings/types"
import CREATE_AUDIO from "../../mutations/CREATE_AUDIO"
import { TransactionMode } from "../../common/enums"
import TRANSLATE_TEXT from "../../mutations/TRANSLATE_TEXT"
import { DownloadHelpers } from "../../common/DownloadHelpers"
import { unparse } from "papaparse"
import { APPROVE_AUDIO } from "../../mutations/APPROVE_AUDIO"
import { BatchItem } from "./BatchItem"
import { Step, useStepControlsContext } from "../../features/StepControls/useStepControlsContext"
import { AudioFileWithLanguage } from "../../common/interfaces"
import { CanStoreVoiceFields } from "../../common/hooks/useNeuralVoiceCheck"

export interface BatchPageContextProps extends CanStoreVoiceFields {
    state: BatchPageState
    dispatch: (action: Action) => void
    pageLoading: boolean
    generateAudio: () => Promise<void>
    translateText: (singleItem?: BatchItem) => Promise<void>
    generateSingleSourceAudio: (item: BatchItem) => Promise<void>
    generateSingleTargetAudio: (item: BatchItem) => Promise<void>
    exportCSV: () => void
    resetPage: () => void
    approveAudio: () => Promise<{ audioFiles: AudioFileWithLanguage[]; hasErrors: boolean }>
    downloadAudio: (audioFiles: AudioFileWithLanguage[]) => void
    allItemsContainAudio: boolean
}

export const BatchPageContext = createContext<BatchPageContextProps>({
    state: getInitialState(),
    dispatch: (action: Action) => {},
    pageLoading: false,
    approveAudio: async () => ({ audioFiles: [], hasErrors: false }),
    generateAudio: async () => {},
    translateText: async () => {},
    generateSingleSourceAudio: async (item: BatchItem) => {},
    generateSingleTargetAudio: async (item: BatchItem) => {},
    exportCSV: () => {},
    resetPage: () => {},
    downloadAudio: (audioFiles: AudioFileWithLanguage[]) => {},
    allItemsContainAudio: false
})

interface BatchPageProviderProps {
    value?: BatchPageContextProps
    children: ReactNode
}

export const BatchPageProvider: FunctionComponent<BatchPageProviderProps> = props => {
    const [state, dispatch] = useReducer(reducer, getInitialState())
    const client = useApolloClient()
    const { deactivate } = useStepControlsContext()

    const getEnabledBatchItems = useCallback(() => state.batchItems.filter(item => item.include), [
        state.batchItems
    ])

    const { refetch: refetchInitialData } = useQuery<{
        availableLanguages: Language[]
        getAudioFormats: AudioFormat[]
    }>(GET_INIT_BATCH_INFO, {
        onCompleted: data => {
            if (data.availableLanguages.length) {
                dispatch(updateAvailableLanguages(data.availableLanguages))
            }
            if (data.getAudioFormats.length) {
                dispatch(updateAvailableAudioFormats(data.getAudioFormats))
                dispatch(updateSelect(Fields.FileFormat, data.getAudioFormats[0].id.toString()))
            }
        }
    })

    const downloadAudio = useCallback(
        (audioFiles: AudioFileWithLanguage[]) => {
            const files = audioFiles.map(file => {
                return {
                    folderName: file.language.name,
                    fileName: file.fileName,
                    binaryData: file.binaryData
                }
            })
            return DownloadHelpers.zipAndSave(files)
        },
        [state]
    )

    const generateSingleSourceAudio = useCallback(
        async (item: BatchItem): Promise<void> => {
            await client
                .mutate({
                    variables: {
                        audioInput: {
                            referenceId: state.referenceId,
                            mode: TransactionMode.SERIAL,
                            formatId: parseInt(state.fields.fileFormat.value),
                            voiceId: parseInt(state.fields.sourceVoice.value),
                            languageId: state.sourceLanguage,
                            fileName: item.name,
                            givenText: item.source
                        }
                    },
                    mutation: CREATE_AUDIO
                })
                .then((response: FetchResult<{ createAudio: AudioFile }>) => {
                    dispatch(setOperationErrors(item.index, { sourceAudioError: undefined }))
                    dispatch(updateItem(item.index, { sourceAudio: response.data?.createAudio }))
                })
                .catch((err: ApolloError) => {
                    dispatch(setOperationErrors(item.index, { sourceAudioError: err.message }))
                    dispatch(updateItem(item.index, item))
                })
        },
        [state]
    )

    const generateSingleTargetAudio = useCallback(
        async (item: BatchItem): Promise<void> => {
            if (item.target) {
                await client
                    .mutate({
                        variables: {
                            audioInput: {
                                referenceId: state.referenceId,
                                mode: TransactionMode.SERIAL,
                                formatId: parseInt(state.fields.fileFormat.value),
                                voiceId: parseInt(state.fields.targetVoice.value),
                                languageId: state.targetLanguage,
                                fileName: item.name,
                                givenText: item.target
                            }
                        },
                        mutation: CREATE_AUDIO
                    })
                    .then((response: FetchResult<{ createAudio: AudioFile }>) => {
                        dispatch(setOperationErrors(item.index, { targetAudioError: undefined }))
                        dispatch(
                            updateItem(item.index, { targetAudio: response.data?.createAudio })
                        )
                    })
                    .catch((err: ApolloError) => {
                        dispatch(setOperationErrors(item.index, { targetAudioError: err.message }))
                        dispatch(updateItem(item.index, item))
                    })
            }
        },
        [state]
    )

    const generateAudio = useCallback(async (): Promise<void> => {
        const sourceQueries = getEnabledBatchItems()
            .filter(item => !item.downloadableSourceAudio)
            .map(item => {
                return {
                    item: item,
                    query: client.mutate({
                        variables: {
                            audioInput: {
                                referenceId: state.referenceId,
                                mode: TransactionMode.SERIAL,
                                formatId: parseInt(state.fields.fileFormat.value),
                                voiceId: parseInt(state.fields.sourceVoice.value),
                                languageId: state.sourceLanguage,
                                fileName: item.name,
                                givenText: item.source
                            }
                        },
                        mutation: CREATE_AUDIO
                    })
                }
            })

        const targetQueries = getEnabledBatchItems()
            .filter(o => o.target && !o.downloadableTargetAudio)
            .map(item => {
                return {
                    item: item,
                    query: client.mutate({
                        variables: {
                            audioInput: {
                                referenceId: state.referenceId,
                                mode: TransactionMode.SERIAL,
                                formatId: parseInt(state.fields.fileFormat.value),
                                voiceId: parseInt(state.fields.targetVoice.value),
                                languageId: state.targetLanguage,
                                fileName: item.name,
                                givenText: item.target
                            }
                        },
                        mutation: CREATE_AUDIO
                    })
                }
            })

        await Promise.all(
            sourceQueries.map(({ item, query }) => {
                return query
                    .then((response: FetchResult<{ createAudio: AudioFile }>) => {
                        dispatch(setOperationErrors(item.index, { sourceAudioError: undefined }))
                        dispatch(
                            updateItem(item.index, { sourceAudio: response.data?.createAudio })
                        )
                    })
                    .catch((err: ApolloError) => {
                        dispatch(setOperationErrors(item.index, { sourceAudioError: err.message }))
                        dispatch(updateItem(item.index, item))
                    })
            })
        )

        await Promise.all(
            targetQueries.map(({ item, query }) => {
                return query
                    .then((response: FetchResult<{ createAudio: AudioFile }>) => {
                        dispatch(setOperationErrors(item.index, { targetAudioError: undefined }))
                        dispatch(
                            updateItem(item.index, { targetAudio: response.data?.createAudio })
                        )
                    })
                    .catch((err: ApolloError) => {
                        dispatch(setOperationErrors(item.index, { targetAudioError: err.message }))
                        dispatch(updateItem(item.index, item))
                    })
            })
        )
    }, [state, getEnabledBatchItems])

    const translateText = async (singleItem?: BatchItem) => {
        const items = singleItem ? [singleItem] : getEnabledBatchItems()
        const translationMutations = items.map(item => {
            return {
                item: item,
                query: client.mutate<{ translateText: { text: string } }>({
                    variables: {
                        translateTextInput: {
                            referenceId: state.referenceId,
                            mode: TransactionMode.SERIAL,
                            text: item.source,
                            sourceLanguage: state.allLanguages[state.sourceLanguage].code,
                            targetLanguage: state.allLanguages[state.targetLanguage].code
                        }
                    },
                    mutation: TRANSLATE_TEXT
                })
            }
        })

        await Promise.all(
            translationMutations.map(({ item, query }) => {
                return query
                    .then((res: FetchResult<{ translateText: { text: string } }>) => {
                        dispatch(setOperationErrors(item.index, { translationError: undefined }))
                        dispatch(
                            updateItem(item.index, {
                                target: res.data?.translateText.text
                            })
                        )
                    })
                    .catch((err: ApolloError) => {
                        dispatch(setOperationErrors(item.index, { translationError: err.message }))
                        dispatch(updateItem(item.index, item))
                    })
            })
        )
    }

    const exportCSV = () => {
        const datestampFormat = "YYYYMMDD-hhmm"
        const baseFilename = state.originalFilename.slice(0, -4) //remove .csv extension from filename
        const filename = `${baseFilename}-modified-${moment().format(datestampFormat)}.csv`
        const charEncoding = "\ufeff"
        const items = state.batchItems.map(item => {
            return {
                name: item.name ?? "",
                source: item.source ?? "",
                target: item.target ?? "",
                enabled: item.include ? "yes" : "no",
                errors: item.errorStringForCSV
            }
        })
        const csvString = unparse(items)
        // char Encoding included for ms excel compatibility, excel default char encoding is different from UTF-8 encoding used by blob, pre-pending the UTF-8 byte order marker allows excel to recognize/use UTF-8 encoding
        // https://stackoverflow.com/a/18925211
        // https://stackoverflow.com/a/17879474
        const blob = new Blob([charEncoding, csvString], { type: "text/csv" })
        DownloadHelpers.createLinkAndClick(blob, filename)
    }

    const approveAudio = useCallback(async () => {
        const audioFilesWithLanguage: AudioFileWithLanguage[] = []
        let hasErrors = false

        const sourceQueries = getEnabledBatchItems()
            .filter(item => item.sourceAudio && !item.downloadableSourceAudio)
            .map(item => {
                return {
                    item: item,
                    query: client.mutate({
                        variables: {
                            approveAudioInput: {
                                referenceId: state.referenceId,
                                mode: TransactionMode.SERIAL,
                                formatId: parseInt(state.fields.fileFormat.value),
                                languageId: state.sourceLanguage,
                                givenText: item.source,
                                voiceId: parseInt(state.fields.sourceVoice.value),
                                audioFile: {
                                    binaryData: item.sourceAudio?.binaryData,
                                    size: item.sourceAudio?.size,
                                    fileName: item.name
                                }
                            }
                        },
                        mutation: APPROVE_AUDIO
                    })
                }
            })

        const targetQueries = getEnabledBatchItems()
            .filter(o => o.targetAudio && !o.downloadableTargetAudio)
            .map(item => {
                return {
                    item: item,
                    query: client.mutate({
                        variables: {
                            approveAudioInput: {
                                referenceId: state.referenceId,
                                mode: TransactionMode.SERIAL,
                                formatId: parseInt(state.fields.fileFormat.value),
                                languageId: state.targetLanguage,
                                givenText: item.target,
                                voiceId: parseInt(state.fields.targetVoice.value),
                                audioFile: {
                                    binaryData: item.targetAudio?.binaryData,
                                    size: item.targetAudio?.size,
                                    fileName: item.name
                                }
                            }
                        },
                        mutation: APPROVE_AUDIO
                    })
                }
            })

        await Promise.all(
            sourceQueries.map(({ item, query }) => {
                return query
                    .then((response: FetchResult<{ approveAudio: AudioFile }>) => {
                        dispatch(setOperationErrors(item.index, { rowIcon: undefined }))
                        dispatch(
                            updateItem(item.index, {
                                downloadableSourceAudio: response.data?.approveAudio
                            })
                        )
                        return Promise.resolve(response.data?.approveAudio)
                    })
                    .catch((err: ApolloError) => {
                        hasErrors = true
                        dispatch(setOperationErrors(item.index, { rowIcon: err.message }))
                        dispatch(updateItem(item.index, item))
                    })
            })
        ).then(data =>
            data.forEach(item => {
                if (!!item)
                    audioFilesWithLanguage.push({
                        ...(item as AudioFile),
                        language: state.allLanguages[state.sourceLanguage]
                    })
            })
        )

        await Promise.all(
            targetQueries.map(({ item, query }) => {
                return query
                    .then((response: FetchResult<{ approveAudio: AudioFile }>) => {
                        dispatch(setOperationErrors(item.index, { rowIcon: undefined }))
                        dispatch(
                            updateItem(item.index, {
                                downloadableTargetAudio: response.data?.approveAudio
                            })
                        )
                        return Promise.resolve(response.data?.approveAudio)
                    })
                    .catch((err: ApolloError) => {
                        hasErrors = true
                        dispatch(setOperationErrors(item.index, { rowIcon: err.message }))
                        dispatch(updateItem(item.index, item))
                    })
            })
        ).then(data =>
            data.forEach(item => {
                if (!!item)
                    audioFilesWithLanguage.push({
                        ...(item as AudioFile),
                        language: state.allLanguages[state.targetLanguage]
                    })
            })
        )

        return { audioFiles: audioFilesWithLanguage, hasErrors: hasErrors }
    }, [state])

    const resetPage = useCallback(() => {
        dispatch(reset())
        refetchInitialData().then((res: any) => {
            if (res.data) {
                dispatch(updateAvailableLanguages(res.data.availableLanguages))
                dispatch(updateSelect(Fields.FileFormat, res.data.getAudioFormats[0].id.toString()))
            }
        })
    }, [])

    const allItemsContainAudio = useMemo(() => {
        return getEnabledBatchItems().every(item => {
            if (item.target) {
                return item.sourceAudio && item.targetAudio
            }
            return item.sourceAudio
        })
    }, [getEnabledBatchItems])

    const batchPageContextValue = useMemo(() => {
        return {
            state,
            dispatch,
            pageLoading: false,
            generateAudio,
            translateText,
            exportCSV,
            approveAudio,
            resetPage,
            downloadAudio,
            generateSingleSourceAudio,
            generateSingleTargetAudio,
            allItemsContainAudio
        }
    }, [state, dispatch, generateAudio, approveAudio, downloadAudio, allItemsContainAudio])

    useEffect(() => {
        if (state.containsValidationErrors || state.containsOperationErrors) {
            deactivate(Step.NEXT)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.containsValidationErrors, state.containsOperationErrors])
    // including deactivate here causes infinite re-render

    return <BatchPageContext.Provider value={props.value ?? batchPageContextValue} {...props} />
}

export const useBatchPageContext = (): BatchPageContextProps => {
    const context = useContext(BatchPageContext)
    if (!context) throw new Error("useBatchPageContext must be used within an BatchPageProvider")
    return context
}
